// Generated by tutorials/record.cjs from tutorials/articles/h23-meal-patterns.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h23 --text-only` (wording) or
// `node tutorials/record.cjs h23` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'meal-patterns',
  catalogId: 'h23',
  title: 'Meal patterns',
  summary: 'A meal pattern is what a resident gets at each meal on top of, or instead of, the menu: their coffee at breakfast, a snack at bedtime. Add an item for one resident, or a global meal pattern your facility shares.',
  audience: 'Dietary managers and staff who keep residents\' standing food preferences',
  area: 'residents',
  learn: [
    'Read a resident\'s meal pattern',
    'Add an item for this resident only',
    'Choose whether the item replaces the menu\'s item or is added to it',
    'Add a global meal pattern, and know when to use one',
    'Change an item, end it on a date or limit it to some days',
    'Delete an item, or break its link to the global meal pattern',
  ],
  keywords: [
    'meal pattern',
    'standing order',
    'always',
    'every day',
    'breakfast',
    'snack',
    'hs snack',
    'nourishment',
    'global meal pattern',
    'preferences',
    'likes',
    'add to menu',
    'days and dates',
    'end date',
    'start date',
    'delete',
    'deactivate',
    'break link',
    'global link',
  ],
  steps: [
    {
      id: 'open',
      part: 'Read a resident\'s meal pattern',
      heading: 'Open the Meal Pattern section',
      text: 'Open the resident and select Meal Pattern on the left. Items are listed by mealtime, with their menu category, consistency, portion and service item.\n\nA plus under MO means the item is added to the menu. Without it, the item takes the place of the menu\'s item in that category.',
      media: {
        image: {
          src: '/help/meal-patterns/open.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/meal-patterns/open.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.6,
        },
        alt: 'The Meal Pattern for Morimoto, Kenji: Coffee at breakfast, Iced Tea at lunch and Graham Crackers at the HS snack.',
      },
    },
    {
      id: 'new-item',
      part: 'Add an item for this resident',
      heading: 'Add an item',
      text: 'Select the arrow next to Add Global, then New (This Resident Only). Pick the mealtime and the menu category, then search for the item.',
      media: {
        image: {
          src: '/help/meal-patterns/new-item.webp',
          width: 1280,
          height: 818,
        },
        clip: {
          src: '/help/meal-patterns/new-item.mp4',
          width: 1280,
          height: 818,
          durationSec: 6,
        },
        alt: 'The Add Meal Pattern Item window: Mealtime Breakfast, Menu Category Cereal and Item Cream of Wheat.',
      },
    },
    {
      id: 'add-to-menu',
      part: 'Add an item for this resident',
      heading: 'Replace the menu item, or add to it',
      text: 'Under Options, Add To Menu says how the item reaches the tray. Standard replaces the menu\'s item in the same category, so Kenji gets cream of wheat instead of the cereal on the menu. Add To Menu gives it as well.',
      media: {
        image: {
          src: '/help/meal-patterns/add-to-menu.webp',
          width: 1280,
          height: 818,
        },
        alt: 'The Options part of the window, with Add To Menu set to Standard (Replaces Menu Cycle Item).',
      },
    },
    {
      id: 'save-item',
      part: 'Add an item for this resident',
      heading: 'Save the item',
      text: 'Select Save. The item is listed under its mealtime and prints on the resident\'s tray ticket from the next meal.',
      media: {
        image: {
          src: '/help/meal-patterns/save-item.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/meal-patterns/save-item.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.4,
        },
        alt: 'The Meal Pattern with Cream of Wheat added under Breakfast, below Coffee.',
      },
    },
    {
      id: 'add-global',
      part: 'Add a global meal pattern',
      heading: 'Open the global list',
      text: 'Select Add Global. You see your facility\'s global meal patterns, each with its group, item and the meals it can be given at. Pick a group or type in Find to narrow the list.',
      media: {
        image: {
          src: '/help/meal-patterns/add-global.webp',
          width: 1280,
          height: 704,
        },
        clip: {
          src: '/help/meal-patterns/add-global.mp4',
          width: 1280,
          height: 704,
          durationSec: 2,
        },
        alt: 'The Global Meal Pattern window listing Decaf Coffee with Meals, Water with Meals, Diabetic HS Snack, Fortified Hot Cereal and a protein shake.',
      },
    },
    {
      id: 'pick-global',
      part: 'Add a global meal pattern',
      heading: 'Check the meals and insert',
      text: 'On the row you want, check the meals the resident gets it at: B, L and D. A snack has a time to pick instead. Then select Insert.',
      media: {
        image: {
          src: '/help/meal-patterns/pick-global.webp',
          width: 1280,
          height: 704,
        },
        clip: {
          src: '/help/meal-patterns/pick-global.mp4',
          width: 1280,
          height: 704,
          durationSec: 6,
        },
        alt: 'Water with Meals with B, L and D checked, ready to insert.',
      },
    },
    {
      id: 'global-rows',
      part: 'Add a global meal pattern',
      heading: 'The global items are on the meal pattern',
      text: 'The items are added at each meal you checked, marked with the globe icon. They stay linked to the global meal pattern, so a change to it reaches every resident who has it.',
      media: {
        image: {
          src: '/help/meal-patterns/global-rows.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/meal-patterns/global-rows.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.6,
        },
        alt: 'The Meal Pattern with Water added at breakfast, lunch and dinner, each marked with the globe icon.',
      },
    },
    {
      id: 'edit-item',
      part: 'Change or end an item',
      heading: 'Open an item to change it',
      text: 'Select the item, then Open. The window is the same as for a new item: change the item, its portion or anything else, then select Save.',
      media: {
        image: {
          src: '/help/meal-patterns/edit-item.webp',
          width: 1280,
          height: 818,
        },
        clip: {
          src: '/help/meal-patterns/edit-item.mp4',
          width: 1280,
          height: 818,
          durationSec: 2,
        },
        alt: 'The Edit Meal Pattern Item window for Cream of Wheat at breakfast, in the Cereal menu category.',
      },
    },
    {
      id: 'days-dates',
      part: 'Change or end an item',
      heading: 'Limit it to some days, or end it',
      text: 'Select Days & Dates. Clear the days of the week the resident should not get the item. Give an End Date for an item that should stop, such as a supplement for six weeks: it prints through that date and then stops on its own. A Start Date holds an item back until that day.',
      media: {
        image: {
          src: '/help/meal-patterns/days-dates.webp',
          width: 1280,
          height: 818,
        },
        clip: {
          src: '/help/meal-patterns/days-dates.mp4',
          width: 1280,
          height: 818,
          durationSec: 6,
        },
        alt: 'The Days & Dates tab with Monday to Friday checked, Saturday and Sunday cleared, and End Date 12/31/2026.',
      },
    },
    {
      id: 'save-days',
      part: 'Change or end an item',
      heading: 'Save the change',
      text: 'Select Save. From the next meal, Kenji gets cream of wheat on weekdays only, until the end of the year.\n\nTo stop an item for a while without losing it, select it and then Deactivate. Activate brings it back.',
      media: {
        image: {
          src: '/help/meal-patterns/save-days.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/meal-patterns/save-days.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.3,
        },
        alt: 'The Meal Pattern with Cream of Wheat still under Breakfast after saving.',
      },
    },
    {
      id: 'delete-item',
      part: 'Change or end an item',
      heading: 'Delete an item',
      text: 'Select the item, then More actions and Delete, and confirm. Any child items go with it. Deleted Items, in the same menu, lists what was deleted.',
      media: {
        image: {
          src: '/help/meal-patterns/delete-item.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/meal-patterns/delete-item.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'The Delete Meal Pattern Item confirmation asking to delete Graham Crackers from the HS snack, with Cancel and Delete.',
      },
    },
    {
      id: 'break-link',
      part: 'Change or end an item',
      heading: 'Break the link to the global meal pattern',
      text: 'A global item follows its global meal pattern. To make it the resident\'s own, so later changes to the global one no longer reach it, select the item, then More actions and Global: Break Link, and select Yes.\n\nGlobal: Reset does the opposite: it puts the resident\'s copy back the way the global meal pattern is.',
      media: {
        image: {
          src: '/help/meal-patterns/break-link.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/meal-patterns/break-link.mp4',
          width: 1280,
          height: 748,
          durationSec: 6,
        },
        alt: 'The Break Global Link confirmation, asking whether to break the selected global link, with No and Yes.',
      },
    },
    {
      id: 'unlinked',
      part: 'Change or end an item',
      heading: 'The item is the resident\'s own',
      text: 'The globe icon is gone from that item. It stays on the meal pattern, and you change it like any item for this resident only.',
      media: {
        image: {
          src: '/help/meal-patterns/unlinked.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/meal-patterns/unlinked.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.6,
        },
        alt: 'The Meal Pattern with Water at breakfast no longer marked with the globe icon, while Water at lunch and dinner still is.',
      },
    },
  ],
  tips: [
    'Use a global meal pattern when many residents get the same thing, such as water with meals or a diabetic bedtime snack. You keep it in one place, on Menus, Global Meal Patterns.',
    'Use an item for this resident only for a personal preference, like Kenji\'s cream of wheat.',
    'Days & Dates in the item window limits an item to some days of the week, or to a date range.',
  ],
  notes: [
    'Standard replaces the menu cycle\'s item in the same menu category. Add To Menu puts the item on the tray as well as the menu. Add When No Similar Item Found adds it only when the menu has nothing like it.',
    'A change to a global meal pattern reaches every resident who has it, except for anything changed for one resident only. To stop an item following the global meal pattern for one resident, break its link.',
    'Some facilities call the meal pattern by another name, such as Standing Order. The section uses your facility\'s name.',
    'Deleting items and breaking or resetting a global link need the right role. If an option is greyed out, ask your administrator.',
  ],
  related: ['allergies-likes-dislikes', 'change-diet-order', 'tray-tickets'],
  recorded: '2026-10-28',
};

export default article;
